// Recidiviz - a data platform for criminal justice reform
// Copyright (C) 2024 Recidiviz, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.
// =============================================================================

import { makeRecordFixture } from "../../../../utils/zod/object/makeRecordFixture";
import { SupervisionStaffRecord, supervisionStaffRecordSchema } from "./schema";

/* These Idaho fixtures are used for Workflows in Demo Mode and Offline Mode,
   as well as for Demo Mode and tests for various parts of Insights. */
export const usIdSupervisionStaffFixtures: Array<
  SupervisionStaffRecord["input"]
> = [
  {
    email: "test-officer-4@example.com",
    district: "DISTRICT 1",
    staffExternalId: "OFFICER4",
    stableStaffExternalId: "STABLE_OFFICER4",
    stateCode: "US_ID",
    givenNames: "Walter",
    surname: "Harris",
    supervisorExternalId: "SUPER847",
    pseudonymizedId: "p004",
    hasCaseload: true,
    stateSpecificData: {
      stateCode: "US_ID" as const,
      officeAddress: {
        line1: "1234 address lane",
        city: "iowa",
        zip: "11123",
      },
    },
  },
  {
    email: "test-officer-6@example.com",
    district: "DISTRICT 1",
    staffExternalId: "OFFICER6",
    stableStaffExternalId: "STABLE_OFFICER6",
    stateCode: "US_ID",
    givenNames: "Hank",
    surname: "Schroeder",
    supervisorExternalId: "SUPER847",
    pseudonymizedId: "p006",
    hasCaseload: true,
    stateSpecificData: {
      stateCode: "US_ID" as const,
      officeAddress: null,
    },
  },
];

/* These fixtures are used for Workflows Demo Mode and Offline Mode. */
export const supervisionStaffFixtures: Array<SupervisionStaffRecord> = [
  // US_CA - CALIFORNIA
  {
    email: "test-officer-1@example.com",
    district: "DISTRICT 1",
    staffExternalId: "OFFICER1",
    stableStaffExternalId: "STABLE_OFFICER1",
    stateCode: "US_CA",
    givenNames: "Maia",
    surname: "Douglas",
    supervisorExternalId: "SUPERVISOR1",
    pseudonymizedId: "p001",
    hasCaseload: true,
  },

  // US_IA - IOWA
  {
    email: "test-officer-1@example.com",
    staffExternalId: "OFFICER1",
    stableStaffExternalId: "STABLE_OFFICER1",
    stateCode: "US_IA",
    givenNames: "Anne",
    surname: "Teak",
    supervisorExternalId: null,
    pseudonymizedId: "p123",
    hasCaseload: true,
  },

  // US_ID - IDAHO
  ...usIdSupervisionStaffFixtures,

  // US_MI - MICHIGAN
  {
    email: "test-officer-8@example.com",
    staffExternalId: "OFFICER8",
    stableStaffExternalId: "STABLE_OFFICER8",
    stateCode: "US_MI",
    givenNames: "Antonia",
    surname: "Bonilla",
    supervisorExternalId: "1290831",
    pseudonymizedId: "p008",
    hasCaseload: true,
  },

  // US_NC - NORTH CAROLINA
  {
    email: null,
    staffExternalId: "NCOFFICER1",
    stableStaffExternalId: "STABLE_NCOFFICER1",
    stateCode: "US_NC",
    givenNames: "Nathalie",
    surname: "Giles",
    supervisorExternalId: null,
    pseudonymizedId: "NCOFFICER1",
    hasCaseload: true,
  },

  // US_ND - NORTH DAKOTA
  {
    email: "test-officer-3@example.com",
    district: "DISTRICT 1",
    staffExternalId: "OFFICER3",
    stableStaffExternalId: "STABLE_OFFICER3",
    stateCode: "US_ND",
    givenNames: "Sumaiya",
    surname: "Osborne",
    supervisorExternalId: null,
    pseudonymizedId: "p003",
    hasCaseload: true,
  },

  // US_NE - NEBRASKA
  {
    email: "test-officer-ne@example.com",
    district: "DISTRICT 1",
    staffExternalId: "NEOFFICER1",
    stableStaffExternalId: "STABLE_NEOFFICER1",
    stateCode: "US_NE",
    givenNames: "Sarah",
    surname: "Mitchell",
    supervisorExternalId: "NESUPERVISOR1",
    pseudonymizedId: "p001",
    hasCaseload: true,
  },

  // US_OR - OREGON
  {
    email: null,
    staffExternalId: "271828",
    stableStaffExternalId: "STABLE_271828",
    stateCode: "US_OR",
    givenNames: "271828",
    surname: "271828",
    supervisorExternalId: null,
    pseudonymizedId: "p271",
    hasCaseload: true,
  },
  {
    email: null,
    staffExternalId: "314159",
    stableStaffExternalId: "STABLE_314159",
    stateCode: "US_OR",
    givenNames: "314159",
    surname: "314159",
    supervisorExternalId: "SUPER221",
    pseudonymizedId: "p314",
    hasCaseload: true,
  },

  // US_PA - PENNSYLVANIA
  {
    email: "test-officer@example.com",
    staffExternalId: "OFFICER1",
    stableStaffExternalId: "STABLE_OFFICER1",
    stateCode: "US_PA",
    givenNames: "Dana",
    surname: "Pruitt",
    supervisorExternalId: "25-1239123A",
    pseudonymizedId: "p001",
    hasCaseload: true,
  },
  {
    email: "test-officer@example.com",
    staffExternalId: "OFFICER2",
    stableStaffExternalId: "STABLE_OFFICER2",
    stateCode: "US_PA",
    givenNames: "Katelyn",
    surname: "Frederick",
    supervisorExternalId: null,
    pseudonymizedId: "p002",
    hasCaseload: true,
  },

  // US_TN - TENNESSEE
  {
    email: "test-officer@example.com",
    district: "Unit 1",
    staffExternalId: "OFFICER1",
    stableStaffExternalId: "STABLE_OFFICER1",
    stateCode: "US_TN",
    givenNames: "Shayla",
    surname: "Rice",
    supervisorExternalId: "agonzalez123",
    pseudonymizedId: "p001",
    hasCaseload: true,
  },

  // US_TX - TEXAS
  {
    email: "tx-test-officer@example.com",
    district: "DISTRICT 1",
    staffExternalId: "OFFICER1",
    stableStaffExternalId: "STABLE_OFFICER1",
    stateCode: "US_TX",
    givenNames: "Link",
    surname: "Linkus",
    supervisorExternalId: "SUPERVISOR1",
    pseudonymizedId: "p001",
    hasCaseload: true,
    stateSpecificData: {
      stateCode: "US_TX" as const,
      isInUnderstaffedOffice: false,
    },
  },
  {
    email: "tx-test-officer-2@example.com",
    district: "DISTRICT 42",
    staffExternalId: "OFFICER2",
    stableStaffExternalId: "STABLE_OFFICER2",
    stateCode: "US_TX",
    givenNames: "Arthur",
    surname: "Jones",
    supervisorExternalId: "SUPERVISOR2",
    pseudonymizedId: "p002",
    hasCaseload: true,
    stateSpecificData: {
      stateCode: "US_TX" as const,
      isInUnderstaffedOffice: true,
    },
  },
  {
    email: "tx-test-officer-3@example.com",
    district: "DISTRICT 23",
    staffExternalId: "OFFICER3",
    stableStaffExternalId: "STABLE_OFFICER3",
    stateCode: "US_TX",
    givenNames: "Amanda",
    surname: "Garcia",
    supervisorExternalId: "SUPERVISOR3",
    pseudonymizedId: "p003",
    hasCaseload: true,
    stateSpecificData: {
      stateCode: "US_TX" as const,
      isInUnderstaffedOffice: false,
    },
  },
  {
    email: "tx-test-officer-4@example.com",
    district: "DISTRICT 81",
    staffExternalId: "OFFICER4",
    stableStaffExternalId: "STABLE_OFFICER4",
    stateCode: "US_TX",
    givenNames: "Stephanie",
    surname: "Smith",
    supervisorExternalId: "SUPERVISOR4",
    pseudonymizedId: "p004",
    hasCaseload: true,
    stateSpecificData: {
      stateCode: "US_TX" as const,
      isInUnderstaffedOffice: true,
    },
  },
  {
    email: "tx-test-officer-5@example.com",
    district: "DISTRICT 42",
    staffExternalId: "OFFICER5",
    stableStaffExternalId: null,
    stateCode: "US_TX",
    givenNames: "Christopher",
    surname: "Johnson",
    supervisorExternalId: "SUPERVISOR5",
    pseudonymizedId: "p005",
    hasCaseload: true,
    stateSpecificData: {
      stateCode: "US_TX" as const,
      isInUnderstaffedOffice: false,
    },
  },

  /* Supervisors for US_TX. These mirror the Insights supervisor fixtures in
     `Insights/SupervisionOfficerSupervisor/fixture.ts`. This supports the
     end-to-end US_TX ARS/ERS approval chain: these supervisors are surfaced in
     the "send to" dropdown, and because their external IDs match the fixtures
     in Insights, navigating to their profile in Insights will actually surface
     the pending review.
     `hasCaseload: false` keeps them out of caseload search while still making
     them visible in the "send to" dropdown. */
  {
    email: "tx-test-supervisor-gonzalez@example.com",
    district: "DISTRICT 1",
    staffExternalId: "agonzalez123",
    stableStaffExternalId: "STABLE_agonzalez123",
    stateCode: "US_TX",
    givenNames: "Alejandro",
    surname: "Gonzalez",
    supervisorExternalId: null,
    pseudonymizedId: "hashed-agonzalez123",
    hasCaseload: false,
    roleSubtype: "SUPERVISION_OFFICER_SUPERVISOR" as const,
  },
  {
    email: "tx-test-supervisor-lee@example.com",
    district: "DISTRICT 23",
    staffExternalId: "dlee456",
    stableStaffExternalId: "STABLE_dlee456",
    stateCode: "US_TX",
    givenNames: "David",
    surname: "Lee",
    supervisorExternalId: null,
    pseudonymizedId: "hashed-dlee456",
    hasCaseload: false,
    roleSubtype: "SUPERVISION_OFFICER_SUPERVISOR" as const,
  },
  {
    email: "tx-test-supervisor-smith@example.com",
    district: "DISTRICT 42",
    staffExternalId: "rsmith789",
    stableStaffExternalId: "STABLE_rsmith789",
    stateCode: "US_TX",
    givenNames: "Rosa",
    surname: "Smith",
    supervisorExternalId: null,
    pseudonymizedId: "hashed-rsmith789",
    hasCaseload: false,
    roleSubtype: "SUPERVISION_OFFICER_SUPERVISOR" as const,
  },
  {
    email: "tx-test-supervisor-thomas@example.com",
    district: "DISTRICT 81",
    staffExternalId: "cthomas321",
    stableStaffExternalId: "STABLE_cthomas321",
    stateCode: "US_TX",
    givenNames: "Charles",
    surname: "Thomas",
    supervisorExternalId: null,
    pseudonymizedId: "hashed-cthomas321",
    hasCaseload: false,
    roleSubtype: "SUPERVISION_OFFICER_SUPERVISOR" as const,
  },
  {
    email: "tx-test-supervisor-matterson@example.com",
    district: "DISTRICT 42",
    staffExternalId: "cmatterson567",
    stableStaffExternalId: "STABLE_cmatterson567",
    stateCode: "US_TX",
    givenNames: "Cora",
    surname: "Matterson",
    supervisorExternalId: null,
    pseudonymizedId: "hashed-cmatterson567",
    hasCaseload: false,
    roleSubtype: "SUPERVISION_OFFICER_SUPERVISOR" as const,
  },
  {
    email: "tx-test-supervisor-williams@example.com",
    district: "DISTRICT 1",
    staffExternalId: "jwilliams345",
    stableStaffExternalId: "STABLE_jwilliams345",
    stateCode: "US_TX",
    givenNames: "Jared",
    surname: "Williams",
    supervisorExternalId: null,
    pseudonymizedId: "hashed-jwilliams345",
    hasCaseload: false,
    roleSubtype: "SUPERVISION_OFFICER_SUPERVISOR" as const,
  },

  // US_UT - UTAH
  {
    email: null,
    staffExternalId: "UTOFFICER1",
    stableStaffExternalId: "STABLE_UTOFFICER1",
    stateCode: "US_UT",
    givenNames: "Sunny",
    surname: "Campana",
    supervisorExternalId: null,
    pseudonymizedId: "p001",
    hasCaseload: true,
  },

  // Typesense scope permutations (apps/staff/e2e/CaseloadScopedKey.spec.ts)
  //
  // One row per branch of the scope resolver, kept apart from the demo rows
  // above so that editing a demo record cannot silently change what the scope
  // tests assert. `E2E_` external ids are the join key the specs use.

  // US_TN — district baseline. Its two peers below share the state but sit in
  // other districts, so a district-scoped filter has something to exclude.
  {
    email: "e2e-tn-officer@example.com",
    district: "E2E DISTRICT 1",
    staffExternalId: "E2E_TN_OFFICER",
    stateCode: "US_TN",
    givenNames: "Rosalind",
    surname: "Ashwood",
    supervisorExternalId: null,
    pseudonymizedId: "hashed-e2e-tn-officer",
    hasCaseload: true,
  },
  {
    email: "e2e-tn-peer@example.com",
    district: "E2E DISTRICT 2",
    staffExternalId: "E2E_TN_PEER",
    stateCode: "US_TN",
    givenNames: "Booker",
    surname: "Nyland",
    supervisorExternalId: null,
    pseudonymizedId: "hashed-e2e-tn-peer",
    hasCaseload: true,
  },
  {
    email: "e2e-tn-override@example.com",
    district: "E2E DISTRICT 1",
    staffExternalId: "E2E_TN_OVERRIDE",
    stateCode: "US_TN",
    givenNames: "Marisol",
    surname: "Quist",
    supervisorExternalId: null,
    pseudonymizedId: "hashed-e2e-tn-override",
    hasCaseload: true,
  },

  // US_TN — supervisor expansion. The supervisor has their own district, so an
  // active workflowsSupervisorSearch OR's the two clauses together. The report
  // uses the PLURAL `supervisorExternalIds`, which is what the mint endpoint's
  // array-contains lookup queries and the only fixture covering it.
  {
    email: "e2e-tn-supervisor@example.com",
    district: "E2E DISTRICT 3",
    staffExternalId: "E2E_TN_SUPERVISOR",
    stateCode: "US_TN",
    givenNames: "Idris",
    surname: "Vantol",
    supervisorExternalId: null,
    pseudonymizedId: "hashed-e2e-tn-supervisor",
    hasCaseload: true,
  },
  {
    email: "e2e-tn-report@example.com",
    district: "E2E DISTRICT 2",
    staffExternalId: "E2E_TN_REPORT",
    stateCode: "US_TN",
    givenNames: "Perpetua",
    surname: "Oyelaran",
    supervisorExternalId: null,
    supervisorExternalIds: ["E2E_TN_SUPERVISOR"],
    pseudonymizedId: "hashed-e2e-tn-report",
    hasCaseload: true,
  },
  // Reports to E2E_TN_LEAD, who deliberately has NO staff record of their own.
  // That is what produces a `none` base scope, leaving the supervisor
  // expansion as the entire filter.
  {
    email: "e2e-tn-report-2@example.com",
    district: "E2E DISTRICT 2",
    staffExternalId: "E2E_TN_REPORT_2",
    stateCode: "US_TN",
    givenNames: "Calder",
    surname: "Mbeki",
    supervisorExternalId: null,
    supervisorExternalIds: ["E2E_TN_LEAD"],
    pseudonymizedId: "hashed-e2e-tn-report-2",
    hasCaseload: true,
  },

  // A supervisor who DOES have a staff record but no caseload of their own —
  // the shape the US_TX supervisor approval flow produces. With no district
  // either, their own scope is `none`, so the supervisor expansion is all they
  // get. Distinct from E2E_TN_LEAD above, who has no record at all.
  {
    email: "e2e-tn-no-caseload@example.com",
    staffExternalId: "E2E_TN_NO_CASELOAD",
    stateCode: "US_TN",
    givenNames: "Odalys",
    surname: "Fennimore",
    supervisorExternalId: null,
    pseudonymizedId: "hashed-e2e-tn-no-caseload",
    hasCaseload: false,
  },
  {
    email: "e2e-tn-report-3@example.com",
    district: "E2E DISTRICT 2",
    staffExternalId: "E2E_TN_REPORT_3",
    stateCode: "US_TN",
    givenNames: "Ellery",
    surname: "Vasquez-Bright",
    supervisorExternalId: null,
    supervisorExternalIds: ["E2E_TN_NO_CASELOAD"],
    pseudonymizedId: "hashed-e2e-tn-report-3",
    hasCaseload: true,
  },

  // US_ID — no district, so the state baseline falls back to own-caseload
  // (byEmail).
  {
    email: "e2e-id-officer@example.com",
    staffExternalId: "E2E_ID_OFFICER",
    stateCode: "US_ID",
    givenNames: "Thandeka",
    surname: "Rourke",
    supervisorExternalId: null,
    pseudonymizedId: "hashed-e2e-id-officer",
    hasCaseload: true,
  },

  // US_MI — district "10" expands to the four "10 - *" location names.
  {
    email: "e2e-mi-officer@example.com",
    district: "10",
    staffExternalId: "E2E_MI_OFFICER",
    stateCode: "US_MI",
    givenNames: "Ambrose",
    surname: "Fitzsimmons",
    supervisorExternalId: null,
    pseudonymizedId: "hashed-e2e-mi-officer",
    hasCaseload: true,
  },

  // US_CA — the branch is roleSubtype, not district. An officer resolves to
  // own-caseload even though they have one; a supervisor resolves unrestricted.
  {
    email: "e2e-ca-officer@example.com",
    district: "E2E DISTRICT 1",
    staffExternalId: "E2E_CA_OFFICER",
    stateCode: "US_CA",
    givenNames: "Delphine",
    surname: "Achebe",
    supervisorExternalId: null,
    roleSubtype: "SUPERVISION_OFFICER" as const,
    pseudonymizedId: "hashed-e2e-ca-officer",
    hasCaseload: true,
  },
  {
    email: "e2e-ca-supervisor@example.com",
    district: "E2E DISTRICT 1",
    staffExternalId: "E2E_CA_SUPERVISOR",
    stateCode: "US_CA",
    givenNames: "Ignatius",
    surname: "Prashad",
    supervisorExternalId: null,
    roleSubtype: "SUPERVISION_OFFICER_SUPERVISOR" as const,
    pseudonymizedId: "hashed-e2e-ca-supervisor",
    hasCaseload: true,
  },
].map((r) => makeRecordFixture(supervisionStaffRecordSchema, r));
